import { useMsal } from '@azure/msal-react';
import { useContext, useEffect, useState } from 'react';
import { ClientConfigContext } from '../clientConfigContext';
import { getClientCorrelationId } from '../logger/logMetadata';

export enum AccessTokenStatus {
    Success = 'success',
    Error = 'error',
    Loading = 'loading',
}

type AccessTokenSuccess = {
    accessToken: string;
    error: undefined;
    status: AccessTokenStatus.Success;
};

type AccessTokenError = {
    accessToken: undefined;
    error: string;
    status: AccessTokenStatus.Error;
};

type AccessTokenLoading = {
    accessToken: undefined;
    error: undefined;
    status: AccessTokenStatus.Loading;
};

type AccessTokenResult = AccessTokenError | AccessTokenSuccess | AccessTokenLoading;

const DEFAULT_RESULT: AccessTokenResult = {
    accessToken: undefined,
    error: undefined,
    status: AccessTokenStatus.Loading,
};

/**
 * Wrapper around the MSAL acquireTokenSilent method to get the access token.
 */
const useAccessToken = (): AccessTokenResult => {
    const [result, setResult] = useState<AccessTokenResult>(DEFAULT_RESULT);
    const { instance, accounts } = useMsal();
    const { b2cTenantConfig } = useContext(ClientConfigContext);
    const scopes = b2cTenantConfig.scopes;

    useEffect(() => {
        if (accounts.length > 0) {
            instance
                .acquireTokenSilent({
                    account: accounts[0],
                    scopes: scopes,
                    correlationId: getClientCorrelationId(),
                })
                .then((authenticationResult) => {
                    setResult({
                        ...DEFAULT_RESULT,
                        accessToken: authenticationResult.accessToken,
                        status: AccessTokenStatus.Success,
                    });
                })
                .catch((error) => {
                    setResult({
                        ...DEFAULT_RESULT,
                        error: (error as Error).message || 'An error occurred while acquiring the access token.',
                        status: AccessTokenStatus.Error,
                    });
                });
        }
    }, [accounts, instance, scopes]);

    return result;
};

export { useAccessToken };
